import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, MinusCircle, XCircle } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n'
import { Badge, StatusPill, type Tone } from '@/ui'
import { useUser } from '../api/reference'
import { statusLabel } from '../labels'

/** ₹ from integer paise; negative in danger colour when `signed`. */
export function Money({ paise, signed, className }: { paise: number | null | undefined; signed?: boolean; className?: string }) {
  const fmt = useFormat()
  if (paise === null || paise === undefined) return <span className="text-muted">—</span>
  return (
    <span className={cn('font-mono tabular', signed && paise < 0 && 'text-danger', signed && paise > 0 && 'text-ok', className)}>
      {signed && paise > 0 ? '+' : ''}
      {fmt.money(paise)}
    </span>
  )
}

/** kg from integer grams. */
export function Kg({ grams, className }: { grams: number | null | undefined; className?: string }) {
  const fmt = useFormat()
  if (grams === null || grams === undefined) return <span className="text-muted">—</span>
  return <span className={cn('font-mono tabular', className)}>{fmt.weight(grams)}</span>
}

/** IST date-time (or date only). */
export function When({ at, dateOnly, relative }: { at: string | null | undefined; dateOnly?: boolean; relative?: boolean }) {
  const fmt = useFormat()
  if (!at) return <span className="text-muted">—</span>
  const full = fmt.dateTime(at)
  return (
    <time dateTime={at} title={full} className="whitespace-nowrap">
      {relative ? fmt.relative(at) : dateOnly ? fmt.date(at) : full}
    </time>
  )
}

export function Code({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-mono text-[13px] tabular', className)}>{children ?? '—'}</span>
}

/** Balanced / not balanced / unknown. */
export function Check({ ok, label }: { ok: boolean | null | undefined; label?: ReactNode }) {
  const icon =
    ok === true ? (
      <CheckCircle weight="fill" className="text-ok" aria-hidden="true" />
    ) : ok === false ? (
      <XCircle weight="fill" className="text-danger" aria-hidden="true" />
    ) : (
      <MinusCircle className="text-muted" aria-hidden="true" />
    )
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px]">
      {icon}
      <span className="sr-only">{ok === true ? 'Yes' : ok === false ? 'No' : 'Unknown'}</span>
      {label}
    </span>
  )
}

/** Status pill with the console's English label (Hindi in the tooltip where we have one). */
export function Status({ status, label }: { status: string | null | undefined; label?: ReactNode }) {
  if (!status) return <span className="text-muted">—</span>
  const l = statusLabel(status)
  return (
    <span title={l.hi ?? undefined}>
      <StatusPill status={status} label={label ?? l.en} />
    </span>
  )
}

export function Stream({ stream }: { stream: string | null | undefined }) {
  if (!stream) return <span className="text-muted">—</span>
  return stream === 'CONV' ? (
    <Badge tone="reserve">Ordinary · साधारण</Badge>
  ) : (
    <Badge tone="accent">Good · अच्छा</Badge>
  )
}

const SEVERITY_TONE: Record<string, Tone> = { CRITICAL: 'danger', HIGH: 'danger', MEDIUM: 'warn', LOW: 'neutral' }
export function Severity({ severity }: { severity: string }) {
  return (
    <Badge tone={SEVERITY_TONE[severity] ?? 'neutral'} className={severity === 'CRITICAL' ? 'ring-1 ring-danger/40' : undefined}>
      {severity}
    </Badge>
  )
}

/** In-app link styled for tables (stops row-click propagation). */
export function RefLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      onClick={(e) => e.stopPropagation()}
      className={cn('font-mono text-[13px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber', className)}
    >
      {children}
    </Link>
  )
}

/** A user id rendered as a name (looked up once, cached). */
/** A person by id: `name` when the record already carries it (no lookup), else looked up. */
export function UserName({
  id,
  name,
  fallback = '—',
}: {
  id: string | null | undefined
  name?: string | null
  fallback?: string
}) {
  const q = useUser(id && !name ? id : null)
  if (!id) return <span className="text-muted">{fallback}</span>
  if (name) return <span>{name}</span>
  if (q.data) return <span>{q.data.name}</span>
  return <span className="font-mono text-[12px] text-muted" title={id}>{id.slice(0, 8)}…</span>
}

/** A KPI tile for dashboards and detail headers. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  to,
  loading,
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info'
  to?: string
  loading?: boolean
}) {
  const body = (
    <>
      <div className="font-mono text-[10.5px] font-bold uppercase tracking-eyebrow text-muted">{label}</div>
      <div
        className={cn(
          'mt-1.5 font-display text-[28px] leading-none tabular',
          tone === 'ok' && 'text-ok',
          tone === 'warn' && 'text-warn',
          tone === 'danger' && 'text-danger',
          tone === 'info' && 'text-info',
          loading && 'animate-pulse text-muted',
        )}
      >
        {loading ? '…' : value}
      </div>
      {hint && <div className="mt-1.5 text-[12.5px] text-muted">{hint}</div>}
    </>
  )
  const cls = 'block rounded-card border border-rule bg-surface px-4 py-3.5'
  return to ? (
    <Link to={to} className={cn(cls, 'transition-colors hover:border-ink/40 focus-visible:border-ink')}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

/** Section card used on detail pages. */
export function Section({
  title,
  eyebrow,
  actions,
  children,
  className,
  bodyClassName,
  id,
}: {
  title: ReactNode
  eyebrow?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  id?: string
}) {
  return (
    <section id={id} className={cn('rounded-card border border-rule bg-surface', className)} aria-labelledby={id ? `${id}-h` : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-rule px-4 py-3">
        <div className="min-w-0">
          {eyebrow && <div className="eyebrow mb-0.5">{eyebrow}</div>}
          <h2 id={id ? `${id}-h` : undefined} className="text-[17px] leading-tight text-ink">
            {title}
          </h2>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className={cn('px-4 py-3', bodyClassName)}>{children}</div>
    </section>
  )
}

/** English label with a muted Hindi line under it. */
export function Bi({ en, hi, className }: { en: ReactNode; hi?: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex flex-col leading-tight', className)}>
      <span>{en}</span>
      {hi && <span lang="hi" className="font-hindi text-[12px] text-muted">{hi}</span>}
    </span>
  )
}

/** Small key/value grid for dense detail panels. */
export function Facts({ items, columns = 3 }: { items: Array<{ label: ReactNode; value: ReactNode; mono?: boolean }>; columns?: 2 | 3 | 4 }) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-3',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-2 md:grid-cols-3',
        columns === 4 && 'grid-cols-2 md:grid-cols-4',
      )}
    >
      {items.map((it, i) => (
        <div key={i} className="min-w-0">
          <dt className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">{it.label}</dt>
          <dd className={cn('mt-0.5 break-words text-[14px] text-ink', it.mono && 'font-mono tabular')}>{it.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  )
}
